/**
 * Backtest detail — tallies, reading rules, and the per-row comparison
 * table (brief §7.13 output shape). The "Flag-less mismatches" filter is
 * the investigation queue: a mismatch WITH flags is usually the documented
 * manual adjustment the flag describes.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Code,
  Flex,
  Heading,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';

const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const STATUS_COLORS = { MATCH: 'green', MISMATCH: 'red', ENGINE_SKIPPED: 'gray', NO_ACTUAL: 'yellow' };

export default function BacktestView({ detail, onOpenInCalculator }) {
  const { run, backtestRows } = detail;
  const expandedBg = useColorModeValue('gray.50', 'gray.700');
  const [filter, setFilter] = useState('MISMATCH');
  const [expandedId, setExpandedId] = useState(null);

  const totals = run.totals || {};
  const tally = totals.tally || {};

  const filtered = useMemo(() => {
    if (!backtestRows) return [];
    switch (filter) {
      case 'ALL': return backtestRows;
      case 'FLAGLESS': return backtestRows.filter((r) => r.status === 'MISMATCH' && (!r.flags || r.flags.length === 0));
      default: return backtestRows.filter((r) => r.status === filter);
    }
  }, [backtestRows, filter]);

  const flaglessCount = useMemo(
    () => (backtestRows || []).filter((r) => r.status === 'MISMATCH' && (!r.flags || r.flags.length === 0)).length,
    [backtestRows],
  );

  return (
    <Box mt={6}>
      <Flex align="baseline" justify="space-between" wrap="wrap" gap={2}>
        <Heading size="md">Backtest — {new Date(run.started_at).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET</Heading>
        <Text fontSize="xs" color="gray.500">
          engine <Code fontSize="xs">{run.engine_version}</Code> · snapshot <Code fontSize="xs">{(run.snapshot_hash || '').slice(0, 12)}…</Code>
        </Text>
      </Flex>

      <StatGroup my={4} flexWrap="wrap" gap={4}>
        <Stat><StatLabel>Match rate</StatLabel><StatNumber>{totals.matchRate != null ? `${totals.matchRate}%` : '—'}</StatNumber></Stat>
        <Stat><StatLabel>Match</StatLabel><StatNumber>{tally.MATCH ?? '—'}</StatNumber></Stat>
        <Stat><StatLabel>Mismatch</StatLabel><StatNumber>{tally.MISMATCH ?? '—'}</StatNumber></Stat>
        <Stat><StatLabel>Flag-less mismatch</StatLabel><StatNumber>{flaglessCount}</StatNumber></Stat>
        <Stat><StatLabel>Skipped / No actual</StatLabel><StatNumber>{(tally.ENGINE_SKIPPED ?? 0) + (tally.NO_ACTUAL ?? 0)}</StatNumber></Stat>
        <Stat><StatLabel>Σ |Δ|</StatLabel><StatNumber>{money(totals.sumAbsDelta)}</StatNumber></Stat>
      </StatGroup>

      <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
        <AlertIcon />
        Reading rules: every mismatch must be explainable as by-design (insurance/EPO/referral flag),
        a profile/data fix, an engine fix, or a policy question. A flag-less mismatch is the only kind
        that demands investigation. Insurance deductions show as deltas equal to the insurance amount —
        that is correct behavior.
      </Alert>

      <ButtonGroup size="xs" isAttached variant="outline" mb={3}>
        {[['MISMATCH', `Mismatches (${tally.MISMATCH ?? 0})`],
          ['FLAGLESS', `Flag-less (${flaglessCount})`],
          ['MATCH', `Matches (${tally.MATCH ?? 0})`],
          ['ENGINE_SKIPPED', `Skipped (${tally.ENGINE_SKIPPED ?? 0})`],
          ['NO_ACTUAL', `No actual (${tally.NO_ACTUAL ?? 0})`],
          ['ALL', `All (${(backtestRows || []).length})`]].map(([key, label]) => (
            <Button key={key} onClick={() => setFilter(key)} isActive={filter === key}>{label}</Button>
        ))}
      </ButtonGroup>

      <Box overflowX="auto" borderWidth="1px" borderRadius="md">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th isNumeric>Row</Th>
              <Th>Client</Th>
              <Th>Primary LO</Th>
              <Th>Month</Th>
              <Th>Status</Th>
              <Th isNumeric>Computed</Th>
              <Th isNumeric>Actual</Th>
              <Th isNumeric>Δ</Th>
              <Th>Flags</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.length === 0 && (
              <Tr><Td colSpan={9}><Text py={3} textAlign="center" color="gray.500">No rows for this filter.</Text></Td></Tr>
            )}
            {filtered.map((r) => (
              <React.Fragment key={r.id}>
                <Tr cursor="pointer" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                  <Td isNumeric>{r.row_number}</Td>
                  <Td maxW="180px" isTruncated title={r.client}>{r.client}</Td>
                  <Td maxW="180px" isTruncated title={r.primary_lo}>{r.primary_lo}</Td>
                  <Td>{r.month}</Td>
                  <Td><Badge colorScheme={STATUS_COLORS[r.status] || 'gray'}>{r.status}</Badge></Td>
                  <Td isNumeric>{money(r.computed_primary)}</Td>
                  <Td isNumeric>{money(r.actual_primary)}</Td>
                  <Td isNumeric fontWeight={r.status === 'MISMATCH' ? 'bold' : undefined}>
                    {r.delta === null || r.delta === undefined ? '—' : money(r.delta)}
                  </Td>
                  <Td>
                    <Flex gap={1} wrap="wrap" maxW="280px">
                      {(r.flags || []).map((fl) => <Tag key={fl} size="sm" colorScheme="orange">{fl}</Tag>)}
                    </Flex>
                  </Td>
                </Tr>
                {expandedId === r.id && (
                  <Tr bg={expandedBg}>
                    <Td colSpan={9}>
                      <Box fontSize="sm" py={1}>
                        <Text><b>Engine formula:</b> <Code fontSize="xs">{r.engine_formula || '—'}</Code></Text>
                        <Text mt={1}><b>Original AH:</b> <Code fontSize="xs">{r.original_formula || '—'}</Code></Text>
                        <Text mt={1}><b>Comp code:</b> <Code fontSize="xs">{r.comp_code || '—'}</Code></Text>
                        <Text mt={1}>
                          <b>Secondary:</b> computed {money(r.computed_secondary)} / actual {money(r.actual_secondary)}
                        </Text>
                        {onOpenInCalculator && (
                          <Button size="xs" mt={2} colorScheme="blue" variant="outline"
                            onClick={(e) => { e.stopPropagation(); onOpenInCalculator(r.row_number); }}>
                            Open in calculator
                          </Button>
                        )}
                      </Box>
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}
